/**
 * mcp-mutation-client — JSON-RPC 2.0 caller for the four
 * `cogo.ontology.*` mutation tools shipped in Phase 439 Plan 03.
 *
 * Envelope template borrowed verbatim from
 * `packages/cogo-kg-repository/src/server-http/mcp-gateway-fallback.ts`
 * so the wire format stays identical (POST JSON-RPC 2.0 with
 * `params: { name, arguments }`). The narrow-by-tool typing here
 * prevents callers from sending `add_block` args to `modify_action`.
 *
 * `-32001` JSON-RPC errors are converted to typed ShaclViolationError
 * (see ./shacl-error.ts). Any other JSON-RPC / HTTP failure is rethrown
 * as a plain Error with a descriptive message.
 */

import {
  ShaclViolationError,
  parseShaclViolationFromJsonRpcError,
} from './shacl-error'

// --- Tool argument shapes (mirror handler inputSchemas) -------------------

export interface AddBlockArgs {
  tenant_id: string
  app_id: string
  block_definition: Record<string, unknown>
}

export interface ModifyActionArgs {
  tenant_id: string
  app_id: string
  action_id: string
  changes: Record<string, unknown>
}

export interface PreviewChangesArgs {
  tenant_id: string
  app_id: string
  block_definition: Record<string, unknown>
  draft_branch?: string
}

export interface MergeBranchArgs {
  tenant_id: string
  app_id: string
  branch_name: string
}

// --- Tool result shapes (mirror handler returns) --------------------------

export interface AddBlockResult {
  ok: true
  inserted_count: number
  subjects: string[]
  audit_source: 'webmcp'
}

export interface ModifyActionResult {
  ok: true
  inserted_count: number
  subjects: string[]
  audit_source: 'webmcp'
}

export interface PreviewChangesResult {
  ok: true
  inserted_count: number
  draft_branch_name: string
  subjects: string[]
  audit_source: 'webmcp'
}

export interface MergeBranchResult {
  ok: true
  promoted_count: number
  audit_source: 'webmcp'
}

// --- Client ---------------------------------------------------------------

export interface McpMutationClientConfig {
  /** Full mcp-gateway URL, e.g. `https://<project>.supabase.co/functions/v1/cogo-mcp-gateway`. */
  url: string
  /** Optional Supabase JWT supplier; null/undefined means no Authorization header. */
  getAuthToken?: () => Promise<string | null | undefined>
  /** Optional fetch override for testing. */
  fetchImpl?: typeof fetch
  /** AbortController deadline (ms). Default: 15000. */
  timeoutMs?: number
}

export interface McpMutationClient {
  addBlock(args: AddBlockArgs): Promise<AddBlockResult>
  modifyAction(args: ModifyActionArgs): Promise<ModifyActionResult>
  previewChanges(args: PreviewChangesArgs): Promise<PreviewChangesResult>
  mergeBranch(args: MergeBranchArgs): Promise<MergeBranchResult>
}

interface JsonRpcSuccess {
  jsonrpc: '2.0'
  id: number | string
  result: { content: Array<{ type: string; text: string }> }
}

interface JsonRpcErrorEnvelope {
  jsonrpc: '2.0'
  id: number | string
  error: { code: number; message: string; data?: unknown }
}

type JsonRpcResponse = JsonRpcSuccess | JsonRpcErrorEnvelope

function isErr(r: JsonRpcResponse): r is JsonRpcErrorEnvelope {
  return 'error' in r
}

export function createMcpMutationClient(
  config: McpMutationClientConfig,
): McpMutationClient {
  return {
    addBlock: (args) => callTool(config, 'cogo.ontology.add_block', args as unknown as Record<string, unknown>) as Promise<AddBlockResult>,
    modifyAction: (args) =>
      callTool(config, 'cogo.ontology.modify_action', args as unknown as Record<string, unknown>) as Promise<ModifyActionResult>,
    previewChanges: (args) =>
      callTool(config, 'cogo.ontology.preview_changes', args as unknown as Record<string, unknown>) as Promise<PreviewChangesResult>,
    mergeBranch: (args) =>
      callTool(config, 'cogo.ontology.merge_branch', args as unknown as Record<string, unknown>) as Promise<MergeBranchResult>,
  }
}

async function callTool(
  config: McpMutationClientConfig,
  tool: string,
  args: Record<string, unknown>,
): Promise<unknown> {
  const fetcher = config.fetchImpl ?? fetch
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (config.getAuthToken) {
    const tok = await config.getAuthToken()
    if (tok) headers.Authorization = `Bearer ${tok}`
  }
  const body = JSON.stringify({
    jsonrpc: '2.0',
    id: Date.now(),
    method: 'tools/call',
    params: { name: tool, arguments: args },
  })
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), config.timeoutMs ?? 15000)
  try {
    const res = await fetcher(config.url, {
      method: 'POST',
      headers,
      body,
      signal: controller.signal,
    })
    if (!res.ok) {
      throw new Error(
        `[mcp-mutation-client] ${tool} HTTP ${res.status} ${res.statusText}`,
      )
    }
    const envelope = (await res.json()) as JsonRpcResponse
    if (isErr(envelope)) {
      const shacl = parseShaclViolationFromJsonRpcError(envelope.error, tool)
      if (shacl) throw shacl
      throw new Error(
        `[mcp-mutation-client] ${tool} JSON-RPC error ${envelope.error.code}: ${envelope.error.message}`,
      )
    }
    const text = envelope.result?.content?.[0]?.text
    if (!text) {
      throw new Error(`[mcp-mutation-client] ${tool} returned empty content`)
    }
    return JSON.parse(text)
  } finally {
    clearTimeout(timer)
  }
}

// Re-export the SHACL error so callers in the ontology surface only need a single import path.
export { ShaclViolationError }
